import type {
  CurrentUser,
  OrgDirectoryAdapter,
  OrgMember,
  OrgMemberSearchParams,
} from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import React from 'react';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';
import { getSessionSnapshot } from './sessionSnapshot';

export type MemberDirectoryItem = Record<string, unknown> & {
  id?: string;
  memberUuid?: string;
  name?: string;
};

type MemberDirectoryCurrentUser = Pick<
  CurrentUser,
  'id' | 'memberUuid' | 'tenantUuid'
> & {
  organizationUuid?: string;
};

type MemberDirectoryResponse = {
  list?: MemberDirectoryItem[];
  total?: number;
};

type MemberDirectoryState = {
  loadedKey: string;
  memberList: MemberDirectoryItem[];
};

const PAGE_SIZE = 100;
const listeners = new Set<() => void>();

let state: MemberDirectoryState = { loadedKey: '', memberList: [] };
let loadingKey = '';
let loadingPromise: Promise<void> | null = null;

const normalizeText = (value: unknown) => String(value ?? '').trim();

const emitChange = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const setState = (nextState: MemberDirectoryState) => {
  state = nextState;
  emitChange();
};

const getLoadKey = (currentUser?: Partial<MemberDirectoryCurrentUser> | null) =>
  [
    normalizeText(currentUser?.tenantUuid),
    normalizeText(currentUser?.organizationUuid),
    normalizeText(currentUser?.memberUuid),
    normalizeText(currentUser?.id),
  ]
    .filter(Boolean)
    .join('|');

const getMemberDirectoryBaseUrl = () => {
  const openServiceUrl = normalizeText(BizAppConfig.appOpenServiceUrl).replace(
    /\/+$/,
    '',
  );
  const gatewayPath = normalizeText(BizAppConfig.appOpenApiGatewayPath).replace(
    /^\/+|\/+$/g,
    '',
  );
  const serviceCode = normalizeText(
    BizAppConfig.appOrganizationAdminCategoryCode,
  ).replace(/^\/+|\/+$/g, '');
  if (!openServiceUrl || !gatewayPath || !serviceCode) {
    throw new Error('开放平台成员目录配置不完整');
  }
  return `${openServiceUrl}/${gatewayPath}/${serviceCode}/api/v1/members`;
};

const requestMemberPage = async (
  pageNo: number,
  memberUuid: string,
  accessToken: string,
): Promise<MemberDirectoryResponse> => {
  const response = await fetch(
    `${getMemberDirectoryBaseUrl()}?pageNo=${pageNo}&pageSize=${PAGE_SIZE}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
        memberUuid,
      },
    },
  );
  if (!response.ok) throw new Error(`成员目录请求失败: ${response.status}`);
  return response.json() as Promise<MemberDirectoryResponse>;
};

export const clearMemberDirectoryCache = () => {
  loadingKey = '';
  loadingPromise = null;
  setState({ loadedKey: '', memberList: [] });
};

export const preloadMemberDirectory = async (
  currentUser?: Partial<MemberDirectoryCurrentUser> | null,
) => {
  const tenantUuid = normalizeText(currentUser?.tenantUuid);
  const organizationUuid = normalizeText(currentUser?.organizationUuid);
  const memberUuid = normalizeText(currentUser?.memberUuid);
  const loadKey = getLoadKey(currentUser);

  if (!tenantUuid || !organizationUuid || !memberUuid || !loadKey) {
    clearMemberDirectoryCache();
    return;
  }
  if (state.loadedKey === loadKey && state.memberList.length > 0) return;
  if (loadingKey === loadKey && loadingPromise) return loadingPromise;

  if (state.loadedKey !== loadKey) setState({ loadedKey: '', memberList: [] });

  loadingKey = loadKey;
  loadingPromise = (async () => {
    const { accessToken } = await fetchOpenPlatformToken({
      tenantUuid,
      organizationUuid,
      memberUuid,
    });
    const memberList: MemberDirectoryItem[] = [];
    let pageNo = 1;

    while (true) {
      const result = await requestMemberPage(pageNo, memberUuid, accessToken);
      const list = result.list || [];
      memberList.push(...list);

      if (
        !list.length ||
        list.length < PAGE_SIZE ||
        pageNo * PAGE_SIZE >= Number(result.total || 0)
      ) {
        break;
      }
      pageNo += 1;
    }

    setState({ loadedKey: loadKey, memberList });
  })()
    .catch(() => {
      setState({ loadedKey: loadKey, memberList: [] });
    })
    .finally(() => {
      if (loadingKey === loadKey) {
        loadingKey = '';
        loadingPromise = null;
      }
    });

  return loadingPromise;
};

const toOrgMember = (member: MemberDirectoryItem): OrgMember | null => {
  const id = normalizeText(member.memberUuid || member.id);
  if (!id) return null;

  return {
    memberUuid: id,
    name: normalizeText(member.name),
    nickname: normalizeText(member.nickname) || undefined,
    username: normalizeText(member.username) || undefined,
    phone: normalizeText(member.phone) || undefined,
    email: normalizeText(member.email) || undefined,
  };
};

const matchesKeyword = (member: OrgMember, keyword?: string) => {
  const normalizedKeyword = normalizeText(keyword).toLowerCase();
  if (!normalizedKeyword) return true;

  return [member.memberUuid, member.name, member.nickname, member.username]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedKeyword));
};

const getCachedOrgMembers = async () => {
  await preloadMemberDirectory(getSessionSnapshot().currentUser);
  return state.memberList
    .map(toOrgMember)
    .filter((member): member is OrgMember => member !== null);
};

export const memberDirectoryAdapter: OrgDirectoryAdapter = {
  getDepartmentTree: async () => [],
  searchMembers: async (params: OrgMemberSearchParams) => {
    const pageNo = Math.max(params.pageNo || 1, 1);
    const pageSize = Math.max(params.pageSize || PAGE_SIZE, 1);
    const members = (await getCachedOrgMembers()).filter((member) =>
      matchesKeyword(member, params.keyword),
    );
    const offset = (pageNo - 1) * pageSize;

    return {
      list: members.slice(offset, offset + pageSize),
      total: members.length,
      pageNo,
      pageSize,
    };
  },
  getMemberById: async (memberUuid: string) => {
    const member = (await getCachedOrgMembers()).find(
      (item) => item.memberUuid === memberUuid,
    );
    if (!member) throw new Error('未找到指定成员');
    return member;
  },
};

export const subscribeMemberDirectory = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getMemberDirectorySnapshot = () => state.memberList;

export const useMemberDirectory = () =>
  React.useSyncExternalStore(
    subscribeMemberDirectory,
    getMemberDirectorySnapshot,
    getMemberDirectorySnapshot,
  );
